import { motion, AnimatePresence } from "framer-motion";
import React, { useState, useEffect, useMemo } from "react";
import {
  BsDownload,  
  BsSearch,
  BsX,
  BsChevronDown,
  BsChevronUp,
  BsFilter,
  BsPlusCircle,
  BsFileMedical,
} from "react-icons/bs";
import { Link, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import Swal from "sweetalert2";
import api from "../../config/configApi";
import Cookies from "js-cookie";
import PatientModal from "../../components/PatientModal/PatientModal";

interface Avaliacao {
  classificacaoRisco: number;
  classificacaoDescricao: string;
  dataAvaliacao: string;
  nomeProfissionalAvaliacao: string;
}

interface MaterialAPI {
  codigoMaterial: string;
  descricao: string;
  valor: number;
  quantidade: number;
  observacao: string;
}

interface PatientAPI {
  idPacienteSad: number;
  nuCpf: string;
  nomePaciente: string;
  ultimaVisita: string;
  proximaVisita: string;
  statusVisitaProgamada: boolean;
  cep: string;
  bairro: string;
  cidade: string;
  regiao: string;
  fisio: string;
  fono: string;
  psico: string;
  nutri: string;
  curativo: boolean;
  alimentacaoEnteral: string;
  regimeCoparticipacao: string;
  observacao: string;
  orgaoVacinacao: string;
  status: string;
  criadoEm: string;
  atualizadoEm: string;
  avaliacoes: Avaliacao[];
  materiais: MaterialAPI[];
}

interface Image {
  imagem: string;
}

interface Material {
  item: string;
  codigo: string;
  valor: number;
  dataInsercao: string;
  status: number;
}

interface Prontuario {
  id: string;
  nuCpf: string;
  cd_paciente: string;
}

// Interface Patient ajustada para compatibilidade com PatientModal
interface MaterialItem {
  codigoMaterial: string;
  quantidade: number;
  observacao: string;
  valor: number;
  descricao?: string;
}

interface Patient {
  id: string;
  cd_paciente: string;
  nome: string;
  nuCpf: string;
  classe: string;
  ultimaVisita: string;
  visitaProgramada: string;
  statusVisitaPrograma: string;
  cep: string;
  bairro: string;
  localidade: string;
  regiao: string;
  fisio: string;
  fono: string;
  psico: string;
  nutri: string;
  curativo: boolean;
  alimentacaoEnteral: string;
  regimeCoparticipacao: string;
  lemedh: MaterialItem[];
  observacao: string;
  vacinacao: string;
  img?: string | Image[];
  dataInsercao: string;
  status?: string;
  orgaoVacinacao?: string;
    dtNascimento?: string; // Adicione esta linha

}

const Dashboard: React.FC = () => {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [materiais, ] = useState<Material[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [prontuarios, ] = useState<Prontuario[]>([]);
  const [expandedPatient, setExpandedPatient] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const itemsPerPage = 10;
  const [editingPatient, setEditingPatient] = useState<Patient | null>(null);
// Altere o estado inicial para ordenar por nome crescente
const [sortConfig, setSortConfig] = useState<{
  key: string;
  direction: string;
}>({ key: "nome", direction: "ascending" });
  const handleImageClick = (imgSrc: string) => {
    setSelectedImage(imgSrc);
  };

  const closeModal2 = () => {
    setSelectedImage(null);
  };

const mapAPIToPatient = (apiPatient: PatientAPI): Patient => {
  // Determinar a classe baseado na última avaliação
  let classe = "BAIXA";
  if (apiPatient.avaliacoes && apiPatient.avaliacoes.length > 0) {
    const ultimaAvaliacao = apiPatient.avaliacoes[apiPatient.avaliacoes.length - 1];
    classe = ultimaAvaliacao.classificacaoDescricao || "BAIXA";
  }

  // Mapear materiais para lemedh no formato correto
  const lemedh: MaterialItem[] = apiPatient.materiais?.map(material => ({
    codigoMaterial: material.codigoMaterial,
    quantidade: material.quantidade,
    observacao: material.descricao,
    valor: material.valor,
    descricao: material.descricao
  })) || [];

  return {
    id: apiPatient.idPacienteSad.toString(),
    cd_paciente: "",
    nome: apiPatient.nomePaciente,
    nuCpf: apiPatient.nuCpf,
    classe: classe,
    ultimaVisita: apiPatient.ultimaVisita || "",
    visitaProgramada: apiPatient.proximaVisita || "",
    statusVisitaPrograma: apiPatient.statusVisitaProgamada ? "CONCLUÍDO" : "PENDENTE",
    cep: apiPatient.cep || "",
    bairro: apiPatient.bairro || "",
    localidade: apiPatient.cidade || "",
    regiao: apiPatient.regiao || "",
    fisio: apiPatient.fisio || "",
    fono: apiPatient.fono || "",
    psico: apiPatient.psico || "",
    nutri: apiPatient.nutri || "",
    curativo: apiPatient.curativo,
    alimentacaoEnteral: apiPatient.alimentacaoEnteral || "NÃO",
    regimeCoparticipacao: apiPatient.regimeCoparticipacao || "",
    lemedh: lemedh,
    observacao: apiPatient.observacao || "",
    vacinacao: apiPatient.orgaoVacinacao || "",
    img: [],
    dataInsercao: apiPatient.criadoEm || new Date().toISOString(),
    status: apiPatient.status || "ATIVO",
    orgaoVacinacao: apiPatient.orgaoVacinacao || "",
    dtNascimento: "" // Inicialmente vazio, será preenchido pela API específica
  };
};

  const obterCdPaciente = (cpf: string): string => {
    if (!cpf) return "";

    const prontuarioDoPaciente = prontuarios.find(
      (prontuario) => prontuario.nuCpf === cpf
    );

    return prontuarioDoPaciente ? prontuarioDoPaciente.cd_paciente : "";
  };

  useEffect(() => {
    const fetchPatients = async () => {
      try {
        const token = localStorage.getItem("Frontend");
        if (!token) {
          throw new Error("Token de autenticação não encontrado");
        }

        const response = await api.get("/sisclinic/sad/pacientes", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        // Mapear os dados da API para o formato interno
        const mappedPatients = response.data.map((apiPatient: PatientAPI) => 
          mapAPIToPatient(apiPatient)
        );

        setPatients(mappedPatients);

      } catch (error) {
        console.error("Erro ao buscar pacientes:", error);

        if (error && typeof error === "object" && "response" in error) {
          const err = error as { response?: { status?: number } };

          if (err.response?.status === 403) {
            Swal.fire({
              icon: "warning",
              title: "Acesso Negado",
              text: "Você não tem permissão para acessar esta lista de pacientes.",
            });
          } else {
            Swal.fire({
              icon: "error",
              title: "Erro ao carregar pacientes",
              text: "Houve um erro ao tentar buscar os pacientes. Tente novamente mais tarde.",
            });
          }
        } else {
          Swal.fire({
            icon: "error",
            title: "Erro inesperado",
            text: "Ocorreu um erro inesperado. Tente novamente.",
          });
        }
      }
    };

    fetchPatients();
  }, []);

  const pacienteTemProntuarios = (cpf: string): boolean => {
    if (!cpf) return false;

    const prontuariosDoPaciente = prontuarios.filter(
      (prontuario) => prontuario.nuCpf === cpf && prontuario.cd_paciente
    );

    return prontuariosDoPaciente.length > 0;
  };

  const contarProntuariosDoPaciente = (cpf: string): number => {
    if (!cpf) return 0;

    const prontuariosDoPaciente = prontuarios.filter(
      (prontuario) => prontuario.nuCpf === cpf && prontuario.cd_paciente
    );

    return prontuariosDoPaciente.length;
  };

  const openModal = () => {
    setIsModalOpen(true);
    setEditingPatient(null);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingPatient(null);
  };

  const calculateTotal = (lemedh: MaterialItem[]) => {
    let total = 0;
    lemedh.forEach((item) => {
      if (item && !isNaN(item.valor)) {
        total += (item.valor * item.quantidade);
      }
    });
    return total;
  };

  // Função para converter MaterialItem[] para string[] para exibição
  const getDescricaoMateriais = (lemedh: MaterialItem[]): string => {
    return lemedh.map(item => item.descricao || item.observacao || "").join(" + ");
  };

  const generateXLS = () => {
    let grandTotal = 0;

    const data = patients.map((patient) => {
      const total = calculateTotal(patient.lemedh);
      grandTotal += total;

      return {
        Nome: patient.nome,
        "Itens LEMEDH": patient.lemedh.map(item => 
          `${item.descricao || item.observacao} (Qtd: ${item.quantidade})`
        ).join(", "),
        "Data de Inserção": new Date(patient.dataInsercao).toLocaleDateString("pt-BR"),
        "Valor Total": !isNaN(total) ? total.toFixed(2) : "0.00",
      };
    });

    data.push({
      Nome: "TOTAL GERAL",
      "Itens LEMEDH": "",
      "Data de Inserção": "",
      "Valor Total": !isNaN(grandTotal) ? grandTotal.toFixed(2) : "0.00",
    });

    const ws = XLSX.utils.json_to_sheet(data);

    const headerCells = ["A1", "B1", "C1", "D1"];
    headerCells.forEach((cell) => {
      if (ws[cell]) {
        ws[cell].s = {
          font: { bold: true },
          border: {
            top: { style: "thin", color: { rgb: "000000" } },
            left: { style: "thin", color: { rgb: "000000" } },
            bottom: { style: "thin", color: { rgb: "000000" } },
            right: { style: "thin", color: { rgb: "000000" } },
          },
        };
      }
    });

    const totalRow = data.length + 1;
    const totalCells = [
      "A" + totalRow,
      "B" + totalRow,
      "C" + totalRow,
      "D" + totalRow,
    ];
    totalCells.forEach((cell) => {
      if (ws[cell]) {
        ws[cell].s = {
          font: { bold: true },
          border: {
            top: { style: "thin", color: { rgb: "000000" } },
            left: { style: "thin", color: { rgb: "000000" } },
            bottom: { style: "thin", color: { rgb: "000000" } },
            right: { style: "thin", color: { rgb: "000000" } },
          },
        };
      }
    });

    const columnWidths = [
      { wpx: 200 },
      { wpx: 300 },
      { wpx: 100 },
      { wpx: 150 },
    ];
    ws["!cols"] = columnWidths;

    const today = new Date();
    const dateString = today.toLocaleDateString("pt-BR").replace(/\//g, "-");

    const fileName = `SAD_Valores_${dateString}.xlsx`;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Totais");
    XLSX.writeFile(wb, fileName);
  };

  const handleEditPatient = (patientId: string) => {
    const patientToEdit = patients.find((patient) => patient.id === patientId);
    if (patientToEdit) {
      setEditingPatient(patientToEdit);
      setIsModalOpen(true);
    }
  };

  const handleProntuarios = (patient: Patient) => {
    navigate("/prontuarios", {
      state: {
        nome: patient.nome,
        cpf: patient.nuCpf,
      },
    });
  };

 const handleProntuarioCompleto = async (patient: Patient) => {
  const formatarNome = (nome: string) => {
    return nome
      .toLowerCase()
      .split(" ")
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
      .join(" ");
  };

  const calcularIdade = (dataNascimento: string): number => {
    const hoje = new Date();
    const nascimento = new Date(dataNascimento);
    let idade = hoje.getFullYear() - nascimento.getFullYear();
    const mesAtual = hoje.getMonth();
    const diaAtual = hoje.getDate();

    if (
      mesAtual < nascimento.getMonth() ||
      (mesAtual === nascimento.getMonth() && diaAtual < nascimento.getDate())
    ) {
      idade--;
    }
    return idade;
  };

  // Primeiro perguntar se é para editar ou criar novo
  Swal.fire({
    title: "Prontuário do Paciente",
    text: `O que deseja fazer para ${formatarNome(patient.nome)}?`,
    showDenyButton: true,
    showCancelButton: true,
    confirmButtonText: "Novo Prontuário",
    denyButtonText: "Editar Existente",
    cancelButtonText: "Cancelar",
    icon: "question"
  }).then(async (result) => {
    if (result.isConfirmed) {
      // Usuário escolheu "Novo Prontuário" - navegar para /Record
      await abrirProntuario(patient, "/Record");
    } else if (result.isDenied) {
      // Usuário escolheu "Editar Existente" - navegar para /RecordEdite
      await abrirProntuario(patient, "/RecordEdite");
    }
    // Se cancelar, não faz nada
  });

  // Função auxiliar para abrir o prontuário
  const abrirProntuario = async (patient: Patient, rota: string) => {
    Swal.fire({
      title: "Buscando dados do paciente...",
      text: "Por favor, aguarde.",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      // Buscar dados completos do paciente pela API
      const token = localStorage.getItem("Frontend");
      if (!token) {
        throw new Error("Token de autenticação não encontrado");
      }

      // Chamar a API para obter os dados completos do paciente pelo CPF
      const response = await api.get(`/sisclinic/pacientes/cpf/${patient.nuCpf}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const pacienteCompleto = response.data;
      
      // Calcular idade com a data de nascimento da API
      let idade = 0;
      if (pacienteCompleto.dtNascimento) {
        idade = calcularIdade(pacienteCompleto.dtNascimento);
      }

      const cdPaciente = obterCdPaciente(patient.nuCpf);

      Swal.close();

      // Salvar os dados nos cookies
      Cookies.set("cdPaciente", cdPaciente || pacienteCompleto.cdPaciente || "");
      Cookies.set("Nome", pacienteCompleto.nmPaciente || patient.nome);
      Cookies.set("NuCPF", patient.nuCpf);
      Cookies.set("idade", String(idade));
      Cookies.set("dtNascimento", pacienteCompleto.dtNascimento || "");

      // Navegar para a rota escolhida (Record ou RecordEdite)
      navigate(rota, {
        state: {
          nome: pacienteCompleto.nmPaciente || patient.nome,
          nuCpf: patient.nuCpf,
          cdPaciente: cdPaciente || pacienteCompleto.cdPaciente || "",
          idade: idade,
          dtNascimento: pacienteCompleto.dtNascimento || "",
        },
      });

    } catch (error) {
      console.error("Erro ao buscar dados do paciente:", error);
      
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível obter os dados completos do paciente. Usando dados disponíveis.",
      }).then(() => {
        // Se falhar, usar os dados básicos
        const idade = patient.dataInsercao
          ? calcularIdade(patient.dataInsercao)
          : 0;

        const cdPaciente = obterCdPaciente(patient.nuCpf);

        Cookies.set("cdPaciente", cdPaciente);
        Cookies.set("Nome", patient.nome);
        Cookies.set("NuCPF", patient.nuCpf);
        Cookies.set("idade", String(idade));

        // Navegar para a rota escolhida mesmo com dados básicos
        navigate(rota, {
          state: {
            nome: patient.nome,
            nuCpf: patient.nuCpf,
            cdPaciente: cdPaciente,
            idade: idade,
          },
        });
      });
    }
  };
};

  const toggleAccordion = (patientId: string) => {
    setExpandedPatient(expandedPatient === patientId ? null : patientId);
  };

  const handleSort = (key: string) => {
    let direction = "ascending";
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === "ascending"
    ) {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const filteredAndSortedPatients = useMemo(() => {
    let filtered = patients.filter((patient) =>
      patient.nome.toLowerCase().includes(search.toLowerCase())
    );

    if (statusFilter !== "all") {
      filtered = filtered.filter((patient) => patient.classe === statusFilter);
    }

    if (sortConfig !== null) {
      filtered.sort((a, b) => {
        const aValue = a[sortConfig.key as keyof Patient] || "";
        const bValue = b[sortConfig.key as keyof Patient] || "";

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          if (aValue < bValue) {
            return sortConfig.direction === "ascending" ? -1 : 1;
          }
          if (aValue > bValue) {
            return sortConfig.direction === "ascending" ? 1 : -1;
          }
        }
        return 0;
      });
    }

    return filtered;
  }, [patients, search, statusFilter, sortConfig]);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPatients = filteredAndSortedPatients.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredAndSortedPatients.length / itemsPerPage);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const getClassBorderColor = (classType: string) => {
    switch (classType) {
      case "BAIXA":
        return "border-l-4 border-green-500";
      case "REDE":
        return "border-l-4 border-blue-500";
      case "MÉDIA":
        return "border-l-4 border-yellow-500";
      case "ALTA":
        return "border-l-4 border-red-500";
      default:
        return "border-l-4 border-gray-500";
    }
  };

  const getClassBadgeColor = (classType: string) => {
    switch (classType) {
      case "BAIXA":
        return "bg-green-100 text-green-800";
      case "REDE":
        return "bg-blue-100 text-blue-800";
      case "MÉDIA":
        return "bg-yellow-100 text-yellow-800";
      case "ALTA":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };


  const getRegimeColor = (regime: string) => {
    switch (regime) {
      case "ISENTO":
        return "bg-purple-100 text-purple-800";
      case "COPARTICIPAÇÃO":
        return "bg-indigo-100 text-indigo-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const handlePatientSaved = () => {
    closeModal();
    window.location.reload();
  };

  // Função auxiliar para converter boolean para exibição
  const formatarCurativo = (curativo: boolean): string => {
    return curativo ? "SIM" : "NÃO";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="flex justify-center items-center mb-8">
        <motion.img
          src="https://i0.wp.com/afrafepsaude.com.br/wp-content/uploads/2021/06/logo.png?w=692&ssl=1"
          alt="Logo AFRAFEP"
          className="w-60 h-auto"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        />
      </div>

      <div className="max-w-7xl mx-auto">
        <motion.div
          className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6 text-white">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold">
                  Sistema de Atendimento Domiciliar
                </h1>
                <p className="text-blue-100 mt-1">
                  Gestão de Pacientes e Materiais
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Link to="/Home">
                  <motion.button
                    className="flex items-center px-4 py-2 bg-white/20 text-white rounded-lg hover:bg-white/30 transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <BsX className="mr-2" size={20} />
                    Voltar
                  </motion.button>
                </Link>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
              <div className="flex flex-wrap gap-3">
                <motion.button
                  onClick={openModal}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-md"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <BsPlusCircle className="mr-2" size={18} />
                  Inclusão SAD
                </motion.button>              
                {currentPatients.length > 0 && (
                  <motion.button
                    onClick={generateXLS}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-md"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <BsDownload className="mr-2" size={18} />
                    Gerar XLS
                  </motion.button>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                <div className="relative w-full md:w-64">
                  <BsSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar paciente..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>

                <div className="relative w-full md:w-48">
                  <BsFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <select
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm appearance-none bg-white"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">Todos os status</option>
                    <option value="BAIXA">Baixa</option>
                    <option value="MÉDIA">Média</option>
                    <option value="ALTA">Alta</option>
                    <option value="REDE">Rede</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
              {filteredAndSortedPatients.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <div className="text-4xl mb-4">😔</div>
                  <p className="text-lg">Nenhum paciente encontrado</p>
                  <p className="text-sm mt-2">
                    Tente ajustar os filtros de busca
                  </p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50 text-gray-700">
                          <th
                            className="px-6 py-4 text-left font-medium cursor-pointer"
                            onClick={() => handleSort("nome")}
                          >
                            <div className="flex items-center">
                              Nome
                              {sortConfig?.key === "nome" && (
                                <span className="ml-1">
                                  {sortConfig.direction === "ascending"
                                    ? "↑"
                                    : "↓"}
                                </span>
                              )}
                            </div>
                          </th>
                          <th className="px-6 py-4 text-left font-medium">
                            Última Visita
                          </th>
                          <th className="px-6 py-4 text-left font-medium">
                            Visita Programada
                          </th>
                          <th className="px-6 py-4 text-left font-medium">
                            Status
                          </th>
                          <th className="px-6 py-4 text-left font-medium">
                            Ações
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {currentPatients.map((patient) => (
                          <React.Fragment key={patient.id}>
                            <motion.tr
                              className={`hover:bg-gray-50 ${getClassBorderColor(
                                patient.classe
                              )}`}
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ duration: 0.3 }}
                            >
                              <td className="px-6 py-4">
                                <div className="flex items-center">
                                  <span className="font-medium text-gray-900">
                                    {patient.nome}
                                  </span>
                                  {patient.classe && (
                                    <span
                                      className={`ml-2 px-2 py-1 text-xs rounded-full ${getClassBadgeColor(
                                        patient.classe
                                      )}`}
                                    >
                                      {patient.classe}
                                    </span>
                                  )}
                                </div>
                                <div className="text-sm text-gray-500 mt-1">
                                  {patient.nuCpf && `CPF: ${patient.nuCpf}`}
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                {patient.ultimaVisita
                                  ? format(
                                      parseISO(patient.ultimaVisita),
                                      "dd/MM/yyyy",
                                      { locale: ptBR }
                                    )
                                  : "—"}
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex flex-col">
                                  {patient.visitaProgramada
                                    ? format(
                                        parseISO(patient.visitaProgramada),
                                        "dd/MM/yyyy",
                                        { locale: ptBR }
                                      )
                                    : "—"}
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex flex-col gap-1">
                                  {patient.regimeCoparticipacao && (
                                    <span
                                      className={`text-xs px-2 py-1 rounded-full ${getRegimeColor(
                                        patient.regimeCoparticipacao
                                      )}`}
                                    >
                                      Regime: {patient.regimeCoparticipacao}
                                    </span>
                                  )}
                                  {patient.curativo && (
                                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                      Curativo
                                    </span>
                                  )}
                                  {patient.alimentacaoEnteral &&
                                    patient.alimentacaoEnteral !== "NÃO" && (
                                      <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full">
                                        Alimentação Enteral
                                      </span>
                                    )}
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex space-x-2">
                                  <motion.button
                                    onClick={() =>
                                      handleEditPatient(patient.id)
                                    }
                                    className="px-3 py-1 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 transition-colors text-sm"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                  >
                                    Editar
                                  </motion.button>
                                  <motion.button
                                    onClick={() => toggleAccordion(patient.id)}
                                    className="px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors text-sm flex items-center"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                  >
                                    {expandedPatient === patient.id ? (
                                      <>
                                        Ocultar <BsChevronUp className="ml-1" />
                                      </>
                                    ) : (
                                      <>
                                        Detalhes{" "}
                                        <BsChevronDown className="ml-1" />
                                      </>
                                    )}
                                  </motion.button>

                                  {pacienteTemProntuarios(patient.nuCpf) && (
                                    <motion.button
                                      onClick={() => handleProntuarios(patient)}
                                      className="px-3 py-1 w-[110px] bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm flex items-center"
                                      whileHover={{ scale: 1.05 }}
                                      whileTap={{ scale: 0.95 }}
                                      title={`${contarProntuariosDoPaciente(
                                        patient.nuCpf
                                      )} evolução(ões)`}
                                    >
                                      Evolução (
                                      {contarProntuariosDoPaciente(
                                        patient.nuCpf
                                      )}
                                      )
                                    </motion.button>
                                  )}

                                  <motion.button
                                    onClick={() =>
                                      handleProntuarioCompleto(patient)
                                    }
                                    className="px-3 py-1 w-[110px] bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors text-sm flex items-center"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    title="Abrir prontuário completo"
                                  >
                                    <BsFileMedical className="mr-1" />
                                    Prontuário
                                  </motion.button>
                                </div>
                              </td>
                            </motion.tr>

                            <AnimatePresence>
                              {expandedPatient === patient.id && (
                                <motion.tr
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: "auto" }}
                                  exit={{ opacity: 0, height: 0 }}
                                  transition={{ duration: 0.3 }}
                                >
                                  <td
                                    colSpan={5}
                                    className="px-6 py-4 bg-gray-50"
                                  >
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                      <div className="bg-white p-4 rounded-lg shadow-sm">
                                        <h3 className="text-sm font-semibold text-gray-700 mb-2">
                                          Informações Pessoais
                                        </h3>
                                        {patient.cep && (
                                          <div className="mb-2">
                                            <p className="text-xs font-medium text-gray-500">
                                              CEP
                                            </p>
                                            <p className="text-sm">
                                              {patient.cep}
                                            </p>
                                          </div>
                                        )}
                                        {patient.bairro && (
                                          <div className="mb-2">
                                            <p className="text-xs font-medium text-gray-500">
                                              Bairro
                                            </p>
                                            <p className="text-sm">
                                              {patient.bairro}
                                            </p>
                                          </div>
                                        )}
                                        {patient.localidade && (
                                          <div className="mb-2">
                                            <p className="text-xs font-medium text-gray-500">
                                              Cidade
                                            </p>
                                            <p className="text-sm">
                                              {patient.localidade}
                                            </p>
                                          </div>
                                        )}
                                        {patient.regiao && (
                                          <div className="mb-2">
                                            <p className="text-xs font-medium text-gray-500">
                                              Região
                                            </p>
                                            <p className="text-sm">
                                              {patient.regiao}
                                            </p>
                                          </div>
                                        )}
                                      </div>

                                      <div className="bg-white p-4 rounded-lg shadow-sm">
                                        <h3 className="text-sm font-semibold text-gray-700 mb-2">
                                          Equipe de Atendimento
                                        </h3>
                                        {patient.fisio && (
                                          <div className="mb-2">
                                            <p className="text-xs font-medium text-gray-500">
                                              Fisioterapeuta
                                            </p>
                                            <p className="text-sm">
                                              {patient.fisio}
                                            </p>
                                          </div>
                                        )}
                                        {patient.fono && (
                                          <div className="mb-2">
                                            <p className="text-xs font-medium text-gray-500">
                                              Fonoaudiólogo
                                            </p>
                                            <p className="text-sm">
                                              {patient.fono}
                                            </p>
                                          </div>
                                        )}
                                        {patient.psico && (
                                          <div className="mb-2">
                                            <p className="text-xs font-medium text-gray-500">
                                              Psicólogo
                                            </p>
                                            <p className="text-sm">
                                              {patient.psico}
                                            </p>
                                          </div>
                                        )}
                                        {patient.nutri && (
                                          <div className="mb-2">
                                            <p className="text-xs font-medium text-gray-500">
                                              Nutricionista
                                            </p>
                                            <p className="text-sm">
                                              {patient.nutri}
                                            </p>
                                          </div>
                                        )}
                                      </div>

                                      <div className="bg-white p-4 rounded-lg shadow-sm">
                                        <h3 className="text-sm font-semibold text-gray-700 mb-2">
                                          Tratamentos
                                        </h3>
                                        {patient.alimentacaoEnteral &&
                                          patient.alimentacaoEnteral !==
                                            "NÃO" && (
                                            <div className="mb-2">
                                              <p className="text-xs font-medium text-gray-500">
                                                Alimentação Enteral
                                              </p>
                                              <p className="text-sm">
                                                {patient.alimentacaoEnteral}
                                              </p>
                                            </div>
                                          )}
                                        {patient.vacinacao && (
                                          <div className="mb-2">
                                            <p className="text-xs font-medium text-gray-500">
                                              Vacinação
                                            </p>
                                            <p className="text-sm">
                                              {patient.vacinacao}
                                            </p>
                                          </div>
                                        )}
                                        {patient.lemedh.length > 0 && (
                                          <div className="mb-2">
                                            <p className="text-xs font-medium text-gray-500">
                                              LEMEDH
                                            </p>
                                            <p className="text-sm">
                                              {getDescricaoMateriais(patient.lemedh)}
                                            </p>
                                            <p className="text-xs font-medium text-green-600 mt-1">
                                              Valor total: R${" "}
                                              {calculateTotal(
                                                patient.lemedh
                                              ).toFixed(2)}
                                            </p>
                                          </div>
                                        )}
                                        {(
                                          <div className="mb-2">
                                            <p className="text-xs font-medium text-gray-500">
                                              Curativo
                                            </p>
                                            <p className="text-sm">
                                              {formatarCurativo(patient.curativo)}
                                            </p>
                                          </div>
                                        )}

                                        <div className="mb-2">
                                          <p className="text-xs font-medium text-gray-500">
                                            Evoluções/Prontuários
                                          </p>
                                          <p className="text-sm font-medium text-blue-600">
                                            {contarProntuariosDoPaciente(
                                              patient.nuCpf
                                            )}{" "}
                                            registro(s)
                                          </p>
                                        </div>
                                      </div>

                                      {patient.observacao && (
                                        <div className="bg-white p-4 rounded-lg shadow-sm md:col-span-2 lg:col-span-3">
                                          <h3 className="text-sm font-semibold text-gray-700 mb-2">
                                            Patologia/Observações
                                          </h3>
                                          <p className="text-sm">
                                            {patient.observacao}
                                          </p>
                                        </div>
                                      )}

                                      {patient.img &&
                                        (Array.isArray(patient.img)
                                          ? patient.img.length > 0
                                          : typeof patient.img === "string" &&
                                            patient.img.trim() !== "") && (
                                          <div className="bg-white p-4 rounded-lg shadow-sm md:col-span-2 lg:col-span-3">
                                            <h3 className="text-sm font-semibold text-gray-700 mb-2">
                                              Imagens
                                            </h3>
                                            <div className="flex flex-wrap gap-2">
                                              {Array.isArray(patient.img) ? (
                                                patient.img
                                                  .map(
                                                    (imgObj) => imgObj.imagem
                                                  )
                                                  .filter(
                                                    (imgSrc) =>
                                                      imgSrc &&
                                                      imgSrc.trim() !== ""
                                                  )
                                                  .map((imgSrc, imgIndex) => (
                                                    <motion.img
                                                      key={imgIndex}
                                                      src={`data:image/png;base64,${imgSrc}`}
                                                      alt={`Imagem ${
                                                        imgIndex + 1
                                                      }`}
                                                      className="w-24 h-24 object-cover rounded border cursor-pointer hover:shadow-md transition-shadow"
                                                      onClick={() =>
                                                        handleImageClick(imgSrc)
                                                      }
                                                      whileHover={{
                                                        scale: 1.05,
                                                      }}
                                                    />
                                                  ))
                                              ) : (
                                                <motion.img
                                                  src={`data:image/png;base64,${patient.img}`}
                                                  alt="Imagem única"
                                                  className="w-24 h-24 object-cover rounded border cursor-pointer hover:shadow-md transition-shadow"
                                                  onClick={() =>
                                                    handleImageClick(
                                                      patient.img as string
                                                    )
                                                  }
                                                  whileHover={{ scale: 1.05 }}
                                                />
                                              )}
                                            </div>
                                          </div>
                                        )}
                                    </div>
                                  </td>
                                </motion.tr>
                              )}
                            </AnimatePresence>
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                    <div className="flex flex-col sm:flex-row items-center justify-between space-y-3 sm:space-y-0">
                      <div className="text-sm text-gray-700">
                        Mostrando {startIndex + 1} a{" "}
                        {Math.min(endIndex, filteredAndSortedPatients.length)}{" "}
                        de {filteredAndSortedPatients.length} pacientes
                      </div>
                      <div className="flex items-center space-x-2">
                        <motion.button
                          onClick={handlePreviousPage}
                          disabled={currentPage === 1}
                          className={`px-3 py-1 rounded-md text-sm ${
                            currentPage === 1
                              ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                              : "bg-blue-600 text-white hover:bg-blue-700"
                          }`}
                          whileHover={{ scale: currentPage === 1 ? 1 : 1.05 }}
                          whileTap={{ scale: currentPage === 1 ? 1 : 0.95 }}
                        >
                          Anterior
                        </motion.button>
                        <span className="text-sm text-gray-700">
                          Página {currentPage} de {totalPages}
                        </span>
                        <motion.button
                          onClick={handleNextPage}
                          disabled={currentPage === totalPages}
                          className={`px-3 py-1 rounded-md text-sm ${
                            currentPage === totalPages
                              ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                              : "bg-blue-600 text-white hover:bg-blue-700"
                          }`}
                          whileHover={{
                            scale: currentPage === totalPages ? 1 : 1.05,
                          }}
                          whileTap={{
                            scale: currentPage === totalPages ? 1 : 0.95,
                          }}
                        >
                          Próxima
                        </motion.button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </motion.div>
      </div>

      <PatientModal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        patient={editingPatient}
        onPatientSaved={handlePatientSaved}
        materiais={materiais}
      />

      {selectedImage && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50"
          onClick={closeModal2}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="relative max-w-4xl max-h-full">
            <motion.button
              onClick={closeModal2}
              className="absolute top-4 right-4 bg-white rounded-full p-2 shadow-lg z-10"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <BsX size={24} />
            </motion.button>
            <motion.img
              src={`data:image/png;base64,${selectedImage}`}
              alt="Imagem ampliada"
              className="max-w-full max-h-full object-contain"
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
            />
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default Dashboard;